import { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import {
  fetchInventoryPurchasedByCategory,
  type InventoryCategoryGroup,
} from '../lib/inventoryPurchasedReport';

/** Purchased stock grouped by category for the control-centre inventory widget. */
export function useInventoryPurchasedReport() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [categories, setCategories] = useState<InventoryCategoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!businessId) {
      setCategories([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchInventoryPurchasedByCategory(businessId);
      setCategories(data);
    } catch (err) {
      console.error('Error loading purchased inventory report:', err);
      setError(err instanceof Error ? err.message : 'Failed to load purchased report');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const totalAmount = categories.reduce((sum, cat) => sum + cat.totalAmount, 0);
  const totalUnits = categories.reduce((sum, cat) => sum + cat.totalUnits, 0);

  return {
    categories,
    totalAmount,
    totalUnits,
    loading,
    error,
    refresh,
  };
}
